import type { integrations as Api } from '@cwos/api-client';
import type { Severity } from '@cwos/ui-shared';
import { listPhase, type ListPhase, type QueryLike } from '../listState.js';

type ConnectionStatus = Api.ConnectionStatus;
type ConnectorInstance = Api.ConnectorInstance;
type ConnectorOverview = Api.ConnectorOverview;
type CredentialFieldMeta = Api.CredentialFieldMeta;
type CredentialSchemaMeta = Api.CredentialSchemaMeta;
type StalenessLevel = Api.StalenessLevel;
type SyncOutcome = Api.SyncOutcome;

/**
 * Pure presentation logic for the Integration Center. Kept separate from the
 * component so the product-level → UI mapping is unit-tested without rendering,
 * mirroring `permissions.ts` / `dashboardLogic.ts`.
 *
 * Everything here is derived from the PRODUCT-LEVEL shapes the API already
 * projected (owner #5050) — this layer never sees an internal state machine
 * value, a credential, or sync-engine telemetry.
 */

/** Maps a product-level connection status to a severity pill variant. */
export function statusTone(status: ConnectionStatus): Severity {
  switch (status) {
    case 'synced':
      return 'good';
    case 'connected':
    case 'validating':
    case 'initial_sync_running':
    case 'reconciling':
    case 'incremental_sync_running':
      return 'sky';
    case 'degraded':
      return 'look';
    case 'authentication_required':
    case 'sync_failed':
      return 'urgent';
    case 'paused':
    case 'disconnecting':
    case 'not_configured':
    default:
      return 'plain';
  }
}

/**
 * The in-progress statuses — the states the screen polls faster on, because
 * they are mid-flight and will settle.
 */
export function isInFlightStatus(status: ConnectionStatus): boolean {
  return (
    status === 'validating' ||
    status === 'initial_sync_running' ||
    status === 'reconciling' ||
    status === 'incremental_sync_running' ||
    status === 'disconnecting'
  );
}

/**
 * The one-line answer to "is all my history in", and — while it is not — what
 * phase it is in. `complete` is the ONLY phase that says yes; every other
 * branch says what is happening instead, because "Connected" beside an
 * unfinished historical import must never read as "your data is all here".
 */
export function historicalSyncCopy(
  complete: boolean,
  phase: Api.HistoricalSyncPhase,
  /**
   * Whether a historical import has ever been ATTEMPTED on this installation.
   *
   * THE DEFECT THIS EXISTS TO FIX. `phase` collapses two different situations
   * into `none`: an installation whose history has never been touched, and one
   * whose import ran, imported records, and then failed. Both rendered
   * "Historical sync has not run yet". On 2026-09-16 that sentence sat on an
   * installation that had imported 908 service events across two attempts —
   * the screen denied work the database plainly held, which is the same class
   * of untruth as reporting an unmeasured value as zero.
   *
   * Derived by the caller from the failure category, which is set only by a
   * run that actually happened.
   */
  attempted = false,
): string | null {
  if (complete) return 'Historical sync complete — all history from your source is imported.';
  switch (phase) {
    case 'backfilling':
      return 'Historical sync in progress — importing everything from your source’s history. This can take a while.';
    case 'verifying':
      return 'Checking what came in against the source before finishing the historical sync.';
    case 'complete':
      return 'Historical sync complete — all history from your source is imported.';
    case 'none':
    default:
      return attempted
        ? 'Historical sync started but did not finish — some history is imported, and the rest is not. Run it again to continue.'
        : 'Historical sync has not run yet — history before the connection date is not imported yet.';
  }
}

/**
 * The sentence beside "Next update", honest about the cadence. `null` when
 * there is nothing to say (not in recurring sync, or the deployment schedule
 * could not be derived) — the caller renders nothing rather than a promise.
 *
 * THE CADENCE IS NEVER INVENTED OR GUESSED HERE. It comes from the same cron
 * the durable sync dispatcher fires on, so a number shown is a number the API
 * actually sustains — and Ritten's published token quota (2 mints/hour,
 * 3/day) is respected because every refresh REUSES the cached token rather
 * than minting one.
 */
export function nextSyncCopy(
  cadenceMinutes: number | null,
  nextScheduledSyncAt: string | null,
  formatTime: (iso: string) => string,
): string | null {
  if (nextScheduledSyncAt === null) return null;
  const at = formatTime(nextScheduledSyncAt);
  if (cadenceMinutes === null) {
    return `Next update: ~${at}.`;
  }
  return `Next update: ~${at} (roughly every ${cadenceMinutes} minutes).`;
}

/**
 * A retry hint for a FAILED historical sync, from the machine-safe error
 * category — never vendor text. `null` when there is no failure to describe.
 */
export function syncFailureCopy(category: string | null): string | null {
  switch (category) {
    case 'authentication_failed':
    case 'insufficient_permissions':
      return 'The source refused these credentials. Replace them and reconnect to retry.';
    case 'reconciliation_short':
      // NOT "it will retry": reconciliation_short is a PERMANENT failure in
      // the durable job's retry contract (only endpoint_unreachable / timeout /
      // rate_limited re-run automatically). Promising a retry that never
      // happens is the exact false reassurance this surface exists to refuse.
      return 'The import came up short of what the source reports — retry the sync to re-run the check.';
    case 'rate_limited':
    case 'endpoint_unreachable':
    case 'timeout':
      return 'The source could not be reached during the last attempt — it will keep retrying.';
    default:
      return category
        ? // NAME THE CONTROL THAT IS ACTUALLY ON SCREEN. `firstSyncActionLabel`
          // renders "Retry sync" whenever a failure category exists — which is
          // exactly when this branch runs — so "press Connect & Sync" sent the
          // reader looking for a button that is not there. "Connect & Sync" is
          // a different control, for an installation that has no connection yet.
          'The historical import did not finish — press Retry sync to run it again.'
        : null;
  }
}

/**
 * Whether this instance may re-press the first sync from the card.
 *
 * THE BUTTON THIS GATES IS THE RETRY PATH THE API ALREADY HAS (2E-1).
 * `beginConnectAndSync` accepts a re-press from `connected` and `degraded` (the
 * two states a failed first sync lands in) and re-enqueues the SAME durable
 * first-sync job; without a control that calls it, a customer whose historical
 * import failed sees "Sync failed — you can retry" and no way to retry, short
 * of disconnecting and walking the whole setup again.
 *
 * Hidden while: history is in (nothing to retry), an import is running (the
 * re-press would 409/refuse), the state is mid-transition (`validating`,
 * `disconnecting`, the initial walk), the credentials were refused
 * (`authentication_required` — the retry there is replacing credentials
 * first, which this gate must not paper over), or the installation is
 * paused/not configured (the server refuses the press).
 */
export function canRetryFirstSync(instance: ConnectorInstance): boolean {
  return (
    !instance.historicalSyncComplete &&
    !instance.syncRunning &&
    instance.status !== 'authentication_required' &&
    instance.status !== 'validating' &&
    instance.status !== 'disconnecting' &&
    instance.status !== 'paused' &&
    instance.status !== 'not_configured' &&
    instance.status !== 'initial_sync_running' &&
    instance.status !== 'reconciling'
  );
}

/**
 * The label for the control that starts — or restarts — the ONE-TIME historical
 * import.
 *
 * THE DEFECT THIS EXISTS FOR. The button was unconditionally called "Retry
 * sync", directly beneath a card that reads "Historical sync has not run yet —
 * history before the connection date is not imported yet." "Retry" names a
 * SECOND attempt at something that already happened. On an installation whose
 * history has never been imported there is nothing to retry, so the screen
 * diagnosed a missing import and then offered no action that reads as starting
 * one. The administrator of the first real installation reported exactly that:
 * that there was no historical-sync control on the page — while the control was
 * on screen the whole time, under a name that hid it. A diagnosis the customer
 * cannot act on is worse than no diagnosis: it is a dead end that looks like a
 * missing feature.
 *
 * THE ACTION IS UNCHANGED. Both labels press the same durable connect-sync job
 * (`beginConnectAndSync` -> `runHistoricalBackfill` -> `completeHistoricalSync`),
 * through the same route, behind the same server-side `integrations:manage`
 * check, under the same one-shot completion guard. Only the word changes, and
 * only where the distinction is real.
 *
 * WHY `syncErrorCategory` AND NOT THE PHASE. A failed historical import also
 * reports `historicalSyncPhase: 'none'` — `phaseOf` returns 'none' for anything
 * that is not complete/backfilling/verifying, so the phase cannot tell "never
 * started" from "started and failed". `syncErrorCategory` is non-null exactly
 * when a backfill row exists whose status is 'failed', which is precisely the
 * case where "Retry" is the honest word.
 */
export function firstSyncActionLabel(instance: ConnectorInstance): string {
  return instance.syncErrorCategory === null ? 'Import historical data' : 'Retry sync';
}

/**
 * A soft, customer-facing note for a delayed/stale connection (or null).
 *
 * FOR AN AVAILABLE CONNECTOR ONLY. `stale` here says "reconnect to resume
 * updates", which is a remedy, and a remedy is a claim that doing the thing
 * works. On a withheld connector it does not: every credential door answers
 * 409. Callers that can be looking at a withheld connector must go through
 * {@link connectorStalenessNote}, which is given the availability and can
 * therefore decline to promise anything.
 */
export function stalenessCopy(staleness: StalenessLevel): string | null {
  switch (staleness) {
    case 'delayed':
      return 'Updates are a little behind — no action needed, we keep retrying.';
    case 'stale':
      return 'This connection has not updated in a while — reconnect to resume updates.';
    case 'current':
    case 'unknown':
    default:
      return null;
  }
}

/**
 * PH-UX-001 part 2 — the sentence the card did not have.
 *
 * In the `degraded` state BOTH of the card's existing explanatory lines are
 * null by construction: `stalenessCopy` returns null for staleness `unknown`
 * (which `resolveInstanceState` sets for every non-healthy base status), and
 * `syncFailureCopy` receives null because `syncErrorCategory` is only set while
 * the historical import is incomplete. So the one state that most needs an
 * explanation was the one state with none — a pill and four buttons, one of
 * which destroys the credential.
 *
 * Everything this says is checked against the engine rather than reassuring by
 * default: a later successful run sets the state back to `connected`
 * (`service.ts` — `succeeded ? 'connected' : 'degraded'`), so "the next one
 * will try again" is a promise the scheduler actually keeps. It deliberately
 * does NOT claim the cause is benign — `unverified` vs `needs_attention` on the
 * history rows below is where that distinction is made, per run.
 */
export function degradedCopy(
  status: ConnectionStatus,
  staleness: StalenessLevel = 'unknown',
): string | null {
  if (status !== 'degraded') return null;
  if (staleness === 'stale') {
    // PH-UX-001 residual: the calm sentence below promises that the next run
    // will retry on its own. Once no import has succeeded inside the
    // staleness threshold, that promise has already been kept and has not
    // helped, so repeating it is a status badge derived from the error code
    // rather than from what has happened since. Name the controls that ARE
    // on this card, in the order they cost the operator least.
    return (
      'No import has succeeded in a while, and the automatic retries have not cleared this on ' +
      'their own. Nothing already imported has been removed. Run Sync now; if that fails too, ' +
      'replace the credentials, and if it still does not clear, contact support.'
    );
  }
  return (
    'The last import did not finish cleanly. Nothing that was already imported has been ' +
    'removed. The next scheduled import will try again on its own, and if it succeeds this ' +
    'clears itself — the run-by-run detail is below.'
  );
}

/** A short label for a history entry's outcome. */
export function outcomeLabel(outcome: SyncOutcome): string {
  switch (outcome) {
    case 'updated':
      return 'Updated';
    case 'no_changes':
      return 'No changes';
    case 'in_progress':
      return 'In progress';
    case 'delayed':
      return 'Delayed';
    case 'unverified':
      // Says what is actually true of the run: it finished, and the count could
      // not be confirmed. NOT "Needs attention" — there is nothing on this page
      // for the operator to do about it (PH-UX-001 part 1b/3).
      return 'Checking totals';
    case 'needs_attention':
      return 'Needs attention';
    default:
      return 'Update';
  }
}

/** A severity variant for a history entry's outcome. */
export function outcomeTone(outcome: SyncOutcome): Severity {
  switch (outcome) {
    case 'updated':
      return 'good';
    case 'no_changes':
      return 'plain';
    case 'in_progress':
      return 'sky';
    case 'delayed':
      return 'look';
    case 'unverified':
      // Amber, not red. A doubt about completeness is not a data-loss event,
      // and painting it urgent is what sent a real customer toward Disconnect.
      return 'look';
    case 'needs_attention':
      return 'urgent';
    default:
      return 'plain';
  }
}

/** Splits a credential schema into secret (write-only) vs non-secret config fields. */
export function splitCredentialFields(schema: CredentialSchemaMeta): {
  secretFields: CredentialFieldMeta[];
  configFields: CredentialFieldMeta[];
} {
  const secretFields: CredentialFieldMeta[] = [];
  const configFields: CredentialFieldMeta[] = [];
  for (const field of schema.fields) {
    if (field.secret) secretFields.push(field);
    else configFields.push(field);
  }
  return { secretFields, configFields };
}

export interface OverallBanner {
  tone: Severity;
  label: string;
  message: string;
}

/**
 * Derives the single top-of-page banner from every connector's product-level
 * instance. Worst state wins: any connection needing attention dominates, then
 * an in-progress connection, then "all current". With no configured connector
 * it is a neutral empty state, never an error.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * A WITHHELD CONNECTOR IS NOT AN ACTION ITEM, AND ITS ABSENCE IS NOT AN INVITE.
 *
 * The banner used to read `needsAttention` off every instance regardless of
 * availability. A connector connected BEFORE it was withheld ages past twice
 * the staleness threshold, the server escalates it to `action_required` with
 * `needsAttention: true`, and the page then led with **"Action required — one
 * connection needs your attention."** There is no attention that helps: every
 * write door on a withheld connector answers 409, so the banner was an
 * instruction to do something the server refuses. Withheld instances are
 * therefore excluded from the attention count; `withheldBannerNote` is what
 * says they exist, and it says it without demanding anything.
 *
 * The empty state has the mirror problem. "Connect a system below" is true
 * only while there is a system below that can be connected — on a deployment
 * where every connector is withheld it is a instruction to a row that refuses.
 * So the empty message is chosen from whether ANY connector is available, not
 * from whether any is connected.
 */
export function overallBanner(connectors: ConnectorOverview[]): OverallBanner {
  const connectable = connectors.filter((c) => connectorAvailability(c).kind === 'available');
  // A WITHHELD connector's instance must never drive the banner's claims.
  // "Connected"/"Your connections are set up" beside a card that says the
  // connection is unavailable would contradict the only actionable view; the
  // attention count above already reads connectable instances only, and the
  // banner must agree with itself.
  const instances = connectable.map((c) => c.instance).filter((i) => i !== null);

  if (instances.length === 0) {
    return connectable.length === 0
      ? {
          tone: 'plain',
          label: 'No connections yet',
          message:
            'None of the systems below can be connected on this deployment yet, so there is ' +
            'nothing to set up here right now.',
        }
      : {
          tone: 'plain',
          label: 'No connections yet',
          message: 'Connect a system below to start bringing its records into Phibian.',
        };
  }

  // The attention count reads CONNECTABLE instances only — see the docblock.
  const attention = connectable
    .map((c) => c.instance)
    .filter((i) => i !== null && i.needsAttention);

  /**
   * PH-UX-001 part 3. "Action required" is a CLAIM THAT AN ACTION EXISTS, and
   * this banner made it for any `needsAttention` instance whatsoever — which
   * includes `degraded`, the state a false collapse verdict produces. The
   * controls rendered underneath in that state are Sync now, Test connection,
   * Replace credentials and Disconnect: the first re-fires the same
   * classification, the second returns "Connection succeeded." beside the
   * amber pill, the third rotates a working key, and the fourth destroys the
   * credential. None of them is a remedy, so the banner was sending an
   * operator to look for a control that is not on the page — and the only one
   * that visibly "does something" is the destructive one (PH-UX-004).
   *
   * So the urgent label is now earned by an actual remedy being on screen:
   *   - `authentication_required` → Replace credentials genuinely fixes it.
   *   - `canRetryFirstSync`       → Retry sync genuinely re-runs it.
   *   - `stale`                   → reconnecting genuinely resumes updates.
   * Anything else that merely "needs attention" gets a calm, true sentence
   * instead of a false imperative.
   */
  // CORE-AUD-220: open Needs Attention items alone are not a remedy on this
  // page; only the connection's own trouble can earn "Action required".
  const importTrouble = attention.filter((i) => i !== null && importMayBeBehind(i));
  const actionable = importTrouble.filter(
    (i) =>
      i !== null &&
      (i.status === 'authentication_required' || canRetryFirstSync(i) || i.staleness === 'stale'),
  );

  if (actionable.length > 0) {
    return {
      tone: 'urgent',
      label: 'Action required',
      message:
        actionable.length === 1
          ? 'One connection needs your attention.'
          : `${actionable.length} connections need your attention.`,
    };
  }

  // CORE-AUD-220: the import is fine, but Needs Attention holds open items
  // about the connection. Say that — never "All current" beside them, and
  // never "the last import did not finish" when it did.
  if (importTrouble.length === 0 && attention.length > 0) {
    const open = attention.reduce((n, i) => n + (i?.openIssues?.total ?? 0), 0);
    return {
      tone: 'look',
      label: 'Open issues',
      message:
        open === 1
          ? 'Needs Attention holds one open issue about your connections. Imports are running.'
          : `Needs Attention holds ${open} open issues about your connections. Imports are running.`,
    };
  }

  if (attention.length > 0) {
    // Needs a look, but there is nothing here to press. Say that plainly
    // rather than manufacturing urgency the page cannot discharge.
    return {
      tone: 'look',
      label: 'Being checked',
      message:
        attention.length === 1
          ? 'One connection’s last import did not finish cleanly. Nothing already imported was ' +
            'removed, and the next scheduled import will try again — there is nothing to do here ' +
            'yet.'
          : `${attention.length} connections’ last imports did not finish cleanly. Nothing ` +
            'already imported was removed, and the next scheduled imports will try again — there ' +
            'is nothing to do here yet.',
    };
  }

  const inFlight = instances.filter((i) => isInFlightStatus(i.status));
  if (inFlight.length > 0) {
    return {
      tone: 'sky',
      label: 'Working',
      message: 'An import is still running — data will appear once it finishes.',
    };
  }

  const synced = instances.filter((i) => i.status === 'synced');
  if (synced.length === instances.length) {
    return {
      tone: 'good',
      label: 'All current',
      message: 'Every connection is current.',
    };
  }

  return {
    tone: 'good',
    label: 'Connected',
    message: 'Your connections are set up.',
  };
}

// ---------------------------------------------------------------------------
// WHICH records an import left behind (F31)
// ---------------------------------------------------------------------------

type RejectedRecordReport = Api.RejectedRecordReport;

/**
 * What an empty (or non-empty) `records` array actually MEANS, decided from the
 * server's own fields rather than from its length.
 *
 * `records: []` is two entirely different sentences and the API says which:
 * `detailAvailable: false` is "we no longer keep which records", and
 * `detailAvailable: true` beside `rejectedCount: 0` is "nothing was left
 * behind". Reading the length alone collapses them into the second — the
 * withheld-rendered-as-absent defect in docs/withholding-contract.md, told
 * about an import an operator is being asked to go and fix.
 *
 * And a non-empty list is two sentences too. `detailTruncated` says the rows
 * are a SAMPLE; a reader who cannot tell a sample from the set will fix the
 * ones they were shown and believe they are finished.
 */
export type RejectedDetail =
  /** The run rejected nothing. The only branch entitled to say so. */
  | { kind: 'none' }
  /**
   * The run rejected records and the identities are gone — pruned past the
   * retention window, or written before the store existed. NEVER an empty list.
   */
  | { kind: 'expired'; count: number; retainedUntil: string | null }
  /** Some of what was rejected, and the list says it is only some. */
  | { kind: 'sample'; shown: number; count: number; retainedUntil: string | null }
  /** All of what was rejected. */
  | { kind: 'complete'; count: number; retainedUntil: string | null };

/**
 * ORDER MATTERS, the same way it does in `fromFlag`.
 *
 * `detailAvailable` is consulted BEFORE the array is looked at, so an expired
 * report can never fall through to the empty branch — including in the shape
 * the server does not currently emit (`detailAvailable: false` with a zero
 * count). Erring towards "we cannot say" is the safe direction; erring towards
 * "nothing was left behind" is the one that gets an operator to stop looking.
 */
export function rejectedDetail(report: RejectedRecordReport): RejectedDetail {
  const count = Math.max(0, report.rejectedCount);
  if (!report.detailAvailable) {
    return { kind: 'expired', count, retainedUntil: report.detailRetainedUntil };
  }
  if (report.records.length === 0) return { kind: 'none' };
  const shown = report.records.length;
  return report.detailTruncated
    ? { kind: 'sample', shown, count, retainedUntil: report.detailRetainedUntil }
    : { kind: 'complete', count, retainedUntil: report.detailRetainedUntil };
}

/** How many rows the payload yielded — what `listPhase` needs, and nothing more. */
export function rejectedRowCount(report: RejectedRecordReport | undefined): number | undefined {
  return report ? report.records.length : undefined;
}

/**
 * WHICH question the panel is answering, expressed in `ListPhase` — not a
 * fourth state machine beside `listPhase` and `figurePhase`.
 *
 * The permission is checked FIRST and the query is never fired without it, so
 * `listPhase` alone would sit on `loading` forever for a supervisor: a disabled
 * react-query result is neither loading nor errored nor holding data, and
 * `listPhase` reads that as pending. The refusal is knowable from the reader's
 * own permission set, so it is stated rather than waited for.
 *
 * A permission set that has not loaded is NOT a refusal — it is `loading`, and
 * one that failed to load is `failed`. Guessing `refused` there would tell an
 * org admin they may not see their own import detail because `/auth/me` was
 * slow; guessing `granted` would fire a request that comes back 403 and land in
 * the same place a beat later. Neither is an answer, so neither is given.
 */
export function rejectedRecordsPhase(
  gate: { canManage: boolean; isLoading: boolean; isError: boolean },
  query: QueryLike,
  report: RejectedRecordReport | undefined,
): ListPhase {
  if (gate.isLoading) return { kind: 'loading' };
  if (gate.isError) return { kind: 'failed' };
  if (!gate.canManage) return { kind: 'refused' };
  return listPhase(query, rejectedRowCount(report));
}

const plural = (n: number, one: string, many: string): string =>
  n === 1 ? `1 ${one}` : `${n} ${many}`;

/** ISO day, so the pure copy is testable without a locale. Screens pass their own. */
export function isoDay(iso: string): string {
  return iso.slice(0, 10);
}

/** The short line above the list — never a bare count. */
export function rejectedDetailHeading(detail: RejectedDetail): string {
  switch (detail.kind) {
    case 'none':
      return 'Nothing was left behind';
    case 'expired':
      return 'We no longer keep which records';
    case 'sample':
      return `Showing ${detail.shown} of ${plural(detail.count, 'record', 'records')}`;
    case 'complete':
      return `${plural(detail.count, 'record', 'records')} left behind`;
  }
}

/**
 * The sentence under it. Each branch says what it knows and refuses the
 * neighbouring claim outright, because the neighbouring claim is the one a
 * reader will otherwise supply for themselves.
 */
export function rejectedDetailSentence(
  detail: RejectedDetail,
  formatDate: (iso: string) => string = isoDay,
): string {
  switch (detail.kind) {
    case 'none':
      return 'This import read every record the source sent.';
    case 'expired':
      return (
        `This import left ${plural(detail.count, 'record', 'records')} behind. We keep the count ` +
        `for as long as the import history exists, and which records only for a while` +
        (detail.retainedUntil ? ` — until ${formatDate(detail.retainedUntil)}` : '') +
        `. That window has passed, so the identities are gone. This is an expired list, not an ` +
        `empty one: the records were left behind whether or not we can still name them.`
      );
    case 'sample':
      return (
        `This import left ${plural(detail.count, 'record', 'records')} behind and ${detail.shown} ` +
        `of them are listed here. The rest were not stored, so this is a sample and not the whole ` +
        `set — fixing everything listed will not have fixed everything.` +
        (detail.retainedUntil ? ` Kept until ${formatDate(detail.retainedUntil)}.` : '')
      );
    case 'complete':
      return (
        `Every record this import left behind is listed.` +
        (detail.retainedUntil
          ? ` Which records is kept until ${formatDate(detail.retainedUntil)}; the count stays after that.`
          : '')
      );
  }
}

// ---------------------------------------------------------------------------
// WHETHER THIS CONNECTOR CAN BE CONNECTED AT ALL (F434)
// ---------------------------------------------------------------------------

/**
 * `GET /integrations/connectors` answers `available` on every connector, and
 * `unavailableReason` — the connector's own customer-safe sentence — on the
 * ones it withholds (`service.ts:getConnectorsOverview`). Neither field used to
 * be in `Api.ConnectorOverview`, so the Integration Center never read them, and
 * a WITHHELD connector rendered exactly like one nobody had got round to
 * connecting yet: the credential form, the "Connect" button, and a pill reading
 * "Not connected".
 *
 * That is the withheld/absent collapse with a live secret in it. The operator
 * pastes a vendor credential into a form whose only possible answer is the
 * 409 `ConnectorUnavailableError` (`routes.ts:227`) — a dead button that costs
 * them a credential rotation to find out about.
 *
 * THE LOCAL RE-DECLARATION OF THOSE TWO FIELDS IS GONE. It was written here
 * because the shared client's type was a subset of the wire; the client now
 * carries both fields properly, and keeping a private copy beside it would be a
 * second, divergent truth about the same response — which is the exact shape
 * that made the screen blind to `available` in the first place. One type,
 * imported.
 */

/** What a connector card is entitled to offer. */
export type ConnectorAvailability =
  | { kind: 'available' }
  /**
   * Withheld by the deployment. No credential form, no Connect button, and the
   * reason said out loud — because "Not connected" and "cannot be connected"
   * are different sentences and only one of them is worth an operator's time.
   */
  | { kind: 'withheld'; reason: string };

/**
 * The fallback sentence, for the shape where the server says `available: false`
 * and sends no summary. Never a reason to render the form.
 */
export const WITHHELD_CONNECTOR_FALLBACK =
  'This connection is unavailable on this deployment. Nothing you did caused it, and ' +
  'there is nothing to enter here yet.';

/**
 * Reads the two fields that say whether this card may offer anything.
 *
 * ABSENT `available` IS TREATED AS AVAILABLE, deliberately and in the direction
 * that is wrong less often. The type now says the key is always there, but a
 * type is a claim about the server and not a guarantee about the bytes: a
 * response that did not come from this API has no `available` at all, and
 * failing closed there would render every connector as withheld and take the
 * Integration Center down for a reason no operator could act on. So the test
 * stays `!== false` rather than tightening to `=== true`. `false` — the value
 * this API actually sends to withhold — is honoured exactly.
 */
/**
 * Is the CONNECTION itself in trouble — so what it synced may be behind?
 * CORE-AUD-220 widened `needsAttention` to include open Needs Attention items
 * about the connection; those say nothing about whether imports are current.
 * An older server sends no `connectionNeedsAttention`, and then the two were
 * the same thing.
 */
export function importMayBeBehind(instance: ConnectorInstance): boolean {
  return instance.connectionNeedsAttention ?? instance.needsAttention;
}

export function connectorAvailability(connector: ConnectorOverview): ConnectorAvailability {
  if (connector.available !== false) return { kind: 'available' };
  const reason = (connector.unavailableReason ?? '').trim();
  return { kind: 'withheld', reason: reason === '' ? WITHHELD_CONNECTOR_FALLBACK : reason };
}

/**
 * The pill on a withheld connector.
 *
 * "Not connected" is the string the card used, and it is the one word this
 * connector must not say: it invites the reader to go and connect it. A
 * withheld connector with a live instance still says "Unavailable" — the
 * instance is readable and disconnectable (both paths deliberately skip
 * `assertConnectorAvailable`), but it is not going to import anything.
 */
export const WITHHELD_CONNECTOR_LABEL = 'Unavailable';

/**
 * The banner must not call a withheld connector's absence "no connections yet",
 * nor a page of withheld connectors "All current".
 *
 * `overallBanner` counts INSTANCES, so a withheld connector nobody ever
 * connected contributes nothing to it and the empty-state sentence — "Connect a
 * system below to start bringing its records into Phibian" — points at a row
 * that cannot be connected. This is the one extra sentence that fact needs.
 */
export function withheldBannerNote(connectors: readonly ConnectorOverview[]): string | null {
  const withheld = connectors.filter((c) => connectorAvailability(c).kind === 'withheld');
  if (withheld.length === 0) return null;
  const names = withheld.map((c) => c.displayName).join(', ');
  return withheld.length === 1
    ? `${names} is unavailable on this deployment and cannot be connected yet.`
    : `${names} are unavailable on this deployment and cannot be connected yet.`;
}

/**
 * The staleness line on ONE connector's card, decided with its availability in
 * hand.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * THE FALSE REMEDY THIS EXISTS TO STOP.
 *
 * A connector connected BEFORE it was withheld keeps its instance, stops
 * importing, and ages past twice the staleness threshold. `stalenessCopy`
 * then said, in the most prominent line on the card:
 *
 *     "This connection has not updated in a while — reconnect to resume
 *      updates."
 *
 * Every door that sentence points at — store credentials, rotate credentials,
 * test the connection, enable it, run a sync — calls `assertConnectorAvailable`
 * and answers 409. So the remedy was not merely unhelpful, it was the one
 * action the server is guaranteed to refuse, offered in place of the true
 * reason, which was sitting unread in the SAME payload. That is the shape the
 * ledger calls a state collapse: WITHHELD rendered as FAILED-and-fixable.
 *
 * What replaces it is not silence. Updates really have stopped and a person
 * watching a number go flat deserves to be told why, so the withheld branch
 * still says the flow stopped — it just declines to promise that anything the
 * reader does will restart it, and points at the reason already on the card
 * rather than restating it (one sentence, one place, so the two cannot drift).
 *
 * `current` and `unknown` stay silent for a withheld connector exactly as they
 * do for an available one: nothing has gone wrong that a line could describe,
 * and `unknown` is "we have no reading", which the withholding notice already
 * accounts for.
 */
export function connectorStalenessNote(
  staleness: StalenessLevel,
  availability: ConnectorAvailability,
): string | null {
  if (availability.kind === 'available') return stalenessCopy(staleness);
  switch (staleness) {
    case 'delayed':
    case 'stale':
      return (
        'Updates from this connection have stopped. Reconnecting will not restart them — ' +
        'the reason above is why, and it is not something you can clear from here.'
      );
    case 'current':
    case 'unknown':
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Stream health (PH-ADV-005)
// ---------------------------------------------------------------------------
//
// `GET /integrations/:id/streams` has always recorded a per-stream verdict;
// until now nothing in `@cwos/api-client` could read it and nothing here
// called it, so a stream could sit at `not_attempted` for days while the
// connector's own status pill still said "Synced". These three functions are
// the presentation half of closing that: a human label and a severity for
// each stream key and completeness verdict the API sends.

/** A customer-facing name for a stream key — connector vocabulary, never a vendor path. */
export function streamKeyLabel(streamKey: string): string {
  switch (streamKey) {
    case 'service_events':
      return 'Encounters';
    case 'clients':
      return 'Client roster';
    case 'workers':
      return 'Staff roster';
    case 'schedule':
      return 'Scheduled visits';
    case 'care_teams':
      return 'Care teams';
    case 'documentation_status':
      return 'Documentation status';
    default:
      return streamKey;
  }
}

/**
 * PH-UX-005 — the connector card subtitle printed `{category} · brings in
 * {capabilities.join(', ')}`: `ehr · brings in service_events, clients, …`.
 * The registry exposes machine keys with no display metadata, so the words
 * live here, beside the stream labels they share.
 */
export function connectorCategoryLabel(category: string): string {
  switch (category) {
    case 'ehr':
    case 'EHR':
      return 'Electronic health record';
    case 'payroll':
      return 'Payroll';
    case 'scheduling':
      return 'Scheduling';
    case 'messaging':
      return 'Messaging';
    default:
      return category.replace(/_/g, ' ');
  }
}

/** The subtitle line for a connector card, in display names only. */
export function connectorSubtitle(category: string, capabilities: readonly string[]): string {
  const brings = capabilities.map(streamKeyLabel).join(', ');
  return brings === ''
    ? connectorCategoryLabel(category)
    : `${connectorCategoryLabel(category)} · brings in ${brings}`;
}

/** A customer-facing sentence for a completeness verdict. */
export function streamCompletenessLabel(completeness: string): string {
  switch (completeness) {
    case 'complete':
      return 'Complete';
    case 'short':
      return 'Fewer than expected';
    case 'unverifiable':
      return 'Current — coverage cannot be confirmed';
    case 'not_attempted':
      return 'Could not be read';
    default:
      return completeness;
  }
}

/** A severity variant for a completeness verdict. */
export function streamCompletenessTone(completeness: string): Severity {
  switch (completeness) {
    case 'complete':
      return 'good';
    case 'unverifiable':
      return 'sky';
    case 'short':
      return 'look';
    case 'not_attempted':
      return 'urgent';
    default:
      return 'plain';
  }
}
